import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import LangLink from './LangLink'
import LanguageSwitcher from './LanguageSwitcher'

// Top-level navigation. Order is unchanged from before; grouping via
// dropdowns was considered and rejected (keyboard/ARIA surface, mobile
// cost) in favour of decluttering the row: language and the admin login
// moved to the utility bar, and the row collapses to one disclosure
// button at/below 1200px so it never wraps.
const NAV_ITEMS = [
  { to: '/', key: 'home' },
  { to: '/about', key: 'about' },
  { to: '/products', key: 'products' },
  { to: '/partners', key: 'partners' },
  { to: '/how-to-order', key: 'howToOrder' },
  { to: '/incoterms-logistics', key: 'incoterms' },
  { to: '/faq', key: 'faq' },
  { to: '/contact', key: 'contact' },
] as const

function Navbar() {
  const { t, i18n } = useTranslation()
  const { pathname } = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)

  // Close the mobile menu on Escape.
  useEffect(() => {
    if (!menuOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  const base = `/${i18n.language}`
  function isActive(to: string) {
    const full = to === '/' ? base : `${base}${to}`
    return to === '/'
      ? pathname === base || pathname === `${base}/`
      : pathname.startsWith(full)
  }

  const email = t('contact.details.emailValue')
  const phone = t('contact.details.phoneValue')

  return (
    <>
      <div className="utility-bar">
        <div className="utility-bar-inner">
          <div className="utility-bar-info">
            <span>{t('nav.utilityLocation')}</span>
            <span className="utility-bar-sep" aria-hidden="true">
              |
            </span>
            <a href={`mailto:${email}`}>{email}</a>
            <span className="utility-bar-sep" aria-hidden="true">
              |
            </span>
            <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a>
          </div>
          <div className="utility-bar-end">
            <LanguageSwitcher variant="navbar" />
          </div>
        </div>
      </div>

      <nav className="navbar" aria-label={t('nav.brandAlt')}>
        <div className="navbar-inner">
          <LangLink to="/" className="navbar-brand" aria-label={t('nav.brandAria')}>
            <img
              src="/intraveo-mark.svg"
              alt=""
              aria-hidden="true"
              className="navbar-mark"
              width={70}
              height={38}
            />
            <span className="navbar-brand-name">{t('nav.brandAlt')}</span>
          </LangLink>

          <button
            type="button"
            className="navbar-toggle"
            aria-expanded={menuOpen}
            aria-controls="navbar-collapse"
            aria-label={t('nav.menu')}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="navbar-toggle-bar" />
            <span className="navbar-toggle-bar" />
            <span className="navbar-toggle-bar" />
          </button>

          <div
            id="navbar-collapse"
            className="navbar-collapse"
            data-open={menuOpen || undefined}
          >
            <ul className="navbar-nav">
              {NAV_ITEMS.map(({ to, key }) => (
                <li key={key}>
                  <LangLink
                    to={to}
                    className={isActive(to) ? 'is-active' : undefined}
                    aria-current={isActive(to) ? 'page' : undefined}
                    onClick={() => setMenuOpen(false)}
                  >
                    {t(`nav.${key}`)}
                  </LangLink>
                </li>
              ))}
            </ul>
            <LangLink
              to="/how-to-order"
              className="btn btn--primary navbar-cta"
              onClick={() => setMenuOpen(false)}
            >
              {t('nav.requestQuote')}
            </LangLink>
          </div>
        </div>
      </nav>
    </>
  )
}

export default Navbar
